import { useCallback, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { CommandCard } from "./CommandCard";
import { CommandProgress } from "./CommandProgress";
import { STAGES, type JourneyCommand, type Stage } from "./journeyData";

const GAP = 16;
const LOCK = 10; // px before deciding horizontal vs vertical
const SWIPE = 60; // px to change card

function useCardWidth() {
  const ref = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setW(e.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const card = w >= 640 ? Math.min(560, w - 160) : w;
  return { ref, w, card };
}

interface Props { cmds: JourneyCommand[]; index: number; phone: string | null; onIndex: (i: number) => void; onStage: (s: Stage) => void }

export function CommandDeck({ cmds, index, phone, onIndex, onStage }: Props) {
  const { ref, w, card } = useCardWidth();
  const track = useRef<HTMLDivElement>(null);
  const drag = useRef<{ id: number; x: number; y: number; dx: number; axis: "x" | "y" | null } | null>(null);
  const swiped = useRef(false);
  const kbd = useRef(false);
  const section = useRef<HTMLElement>(null);
  const base = (w - card) / 2 - index * (card + GAP);
  const go = useCallback((i: number) => onIndex(Math.max(0, Math.min(cmds.length - 1, i))), [cmds.length, onIndex]);

  useEffect(() => { if (track.current) track.current.style.transform = `translate3d(${base}px,0,0)`; }, [base]);
  useLayoutEffect(() => { // the deck takes the active card's height, so short cards don't leave a gap
    const el = track.current?.children[index] as HTMLElement | undefined;
    const wrap = ref.current;
    if (!el || !wrap) return;
    const fit = () => { wrap.style.height = `${el.offsetHeight}px`; };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [index, card, ref]);

  const down = (e: PointerEvent) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    drag.current = { id: e.pointerId, x: e.clientX, y: e.clientY, dx: 0, axis: null };
  };
  const move = (e: PointerEvent) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    const dx = e.clientX - d.x, dy = e.clientY - d.y;
    if (!d.axis) {
      if (Math.abs(dx) < LOCK && Math.abs(dy) < LOCK) return;
      d.axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y"; // direction lock: vertical scroll never becomes a swipe
      if (d.axis === "x") {
        try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* capture is optional; the swipe still works */ }
        track.current?.classList.add("j-dragging");
      }
    }
    if (d.axis !== "x") return;
    d.dx = dx;
    const edge = (index === 0 && dx > 0) || (index === cmds.length - 1 && dx < 0) ? 0.3 : 1;
    if (track.current) track.current.style.transform = `translate3d(${base + dx * edge}px,0,0)`; // no React re-render while dragging
  };
  const up = (e: PointerEvent) => {
    const d = drag.current;
    drag.current = null;
    if (!d || d.id !== e.pointerId) return;
    track.current?.classList.remove("j-dragging");
    try {
      const el = e.currentTarget as HTMLElement;
      if (el.hasPointerCapture?.(e.pointerId)) el.releasePointerCapture(e.pointerId);
    } catch { /* already released */ }
    swiped.current = d.axis === "x";
    if (d.axis === "x" && Math.abs(d.dx) > SWIPE) go(index + (d.dx < 0 ? 1 : -1));
    else if (track.current) track.current.style.transform = `translate3d(${base}px,0,0)`;
  };
  const key = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    if ((e.target as HTMLElement).closest("input, textarea")) return;
    e.preventDefault();
    kbd.current = true;
    go(index + (e.key === "ArrowRight" ? 1 : -1));
  };
  useEffect(() => { // the old card turns inert after a key move; keep focus inside the deck
    if (kbd.current) { kbd.current = false; section.current?.focus({ preventScroll: true }); }
  }, [index]);
  const nextStageFor = (i: number): Stage | null => {
    const c = cmds[i], n = cmds[i + 1];
    return n && n.stage.id !== c.stage.id ? STAGES.find((s) => s.id === n.stage.id) ?? null : null;
  };

  return (
    <section ref={section} aria-roledescription="carousel" aria-label="Command deck" data-testid="command-deck" onKeyDown={key} tabIndex={-1} className="outline-none">
      <div ref={ref} className="j-deck relative overflow-hidden transition-[height] duration-300 motion-reduce:transition-none" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
        onClickCapture={(e) => { if (swiped.current) { e.preventDefault(); e.stopPropagation(); swiped.current = false; } }}
        style={{ touchAction: "pan-y" }}>
        {w > 0 && (
          <div ref={track} className="j-track flex items-start" style={{ gap: GAP, width: cmds.length * (card + GAP) }}>
            {cmds.map((c, i) => (
              <div key={c.code} style={{ width: card, flex: "0 0 auto" }} onClick={i !== index ? () => go(i) : undefined}
                className={i !== index ? "cursor-pointer" : undefined}>
                <CommandCard c={c} index={i} total={cmds.length} phone={phone} active={i === index} nextStage={nextStageFor(i)} onNextStage={onStage} />
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="mx-auto mt-4 flex max-w-[560px] items-center justify-between gap-3">
        <button type="button" onClick={() => go(index - 1)} disabled={index === 0} data-testid="command-deck-prev" aria-label="Previous command"
          className="j-press inline-flex h-12 w-14 items-center justify-center border-[3px] border-[#FFFFFF] text-[#FFFFFF] disabled:border-[#3A3F48] disabled:text-[#5A606B]">
          <ChevronLeft className="h-6 w-6" aria-hidden /></button>
        <p className="text-center text-xs text-[#B8BEC8]">Swipe, use the arrows or your ← → keys</p>
        <button type="button" onClick={() => go(index + 1)} disabled={index === cmds.length - 1} data-testid="command-deck-next" aria-label="Next command"
          className="j-press inline-flex h-12 w-14 items-center justify-center border-[3px] border-[#C92E2B] bg-[#C92E2B] text-[#FFFFFF] disabled:border-[#3A3F48] disabled:bg-transparent disabled:text-[#5A606B]">
          <ChevronRight className="h-6 w-6" aria-hidden /></button>
      </div>
      <div className="mx-auto max-w-[560px]"><CommandProgress cmds={cmds} index={index} onGo={go} /></div>
    </section>
  );
}
